import { useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { publicTenantsApi } from '@/lib/api/public-tenants';

const STORAGE_KEY = 'mycity.tenantId';
const listeners = new Set<() => void>();

function readStoredTenantId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist a manual city choice (Profile → City). Pass null to clear. */
export function setStoredTenantId(tenantId: string | null): void {
  try {
    if (tenantId) localStorage.setItem(STORAGE_KEY, tenantId);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private-mode storage failures are non-fatal — the auto fallback applies.
  }
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** The persisted manual choice, reactive across the app. */
export function useStoredTenantId(): string | null {
  return useSyncExternalStore(subscribe, readStoredTenantId, () => null);
}

export interface TenantResolution {
  tenantId: string | null;
  /** True while the public tenant list is still loading (and no profile binding). */
  isLoading: boolean;
  /** True when the tenant list failed to load AND no fallback id exists. */
  isError: boolean;
  /** True when the list loaded but no municipality is onboarded. */
  isEmpty: boolean;
  retry: () => void;
}

/**
 * The tenant (municipality) whose city data the signed-in citizen sees,
 * plus the resolution state so screens can show real error/empty UI
 * instead of infinite skeletons.
 *
 * City selection is AUTOMATIC (final-design decision), resolved as:
 *   1. profile.user.tenantId / profile.current.tenantId (from /auth/me)
 *   2. the user's manual choice from Profile → City (localStorage) —
 *      validated against the live list when it's available, trusted
 *      as-is when the list request failed (a flaky network must not
 *      brick a returning user)
 *   3. the first public tenant (single-city pilot fallback)
 */
export function useTenantResolution(): TenantResolution {
  const profile = useAuthStore((s) => s.profile);
  const fromProfile = profile?.user?.tenantId ?? profile?.current?.tenantId ?? null;
  const stored = useStoredTenantId();

  const tenants = useQuery({
    queryKey: ['public-tenants'],
    queryFn: () => publicTenantsApi.list(),
    staleTime: 5 * 60_000,
    enabled: !fromProfile,
  });

  let tenantId: string | null = null;
  if (fromProfile) {
    tenantId = fromProfile;
  } else if (tenants.data) {
    tenantId =
      stored && tenants.data.some((t) => t.id === stored) ? stored : (tenants.data[0]?.id ?? null);
  } else if (tenants.isError) {
    tenantId = stored;
  }

  return {
    tenantId,
    isLoading: !fromProfile && tenants.isPending,
    isError: !fromProfile && tenants.isError && !stored,
    isEmpty: !fromProfile && tenants.data !== undefined && tenants.data.length === 0,
    retry: () => void tenants.refetch(),
  };
}

/** Shorthand for screens that only need the resolved id. */
export function useTenantId(): string | null {
  return useTenantResolution().tenantId;
}

/** Human-readable name of a tenant id (for the Home location indicator). */
export function useTenantName(tenantId: string | null): string | null {
  const tenants = useQuery({
    queryKey: ['public-tenants'],
    queryFn: () => publicTenantsApi.list(),
    staleTime: 5 * 60_000,
    enabled: Boolean(tenantId),
  });
  if (!tenantId) return null;
  return tenants.data?.find((t) => t.id === tenantId)?.name ?? null;
}
